import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../../Utils/persistHelpers"
import { logout } from "../../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../../User/redux/user.actions"
import { REQUESTS_WITHDRAWAL_SLICE_NAME } from "./requestsWithdrawal.constants"
import {
  deleteRequestsWithdrawal,
  getPayoutMethod,
  getRequestsWithdrawal,
  setStatus
} from "./requestsWithdrawal.thunks"

/* ------------- Initial State ------------- */

const columnsAdmin = [
  { field_id: "created_timestamp", label: "Created date", active: true },
  { field_id: "user_full_name", label: "User full name", active: true },
  { field_id: "user_email", label: "User email", active: true },
  { field_id: "amount", label: "Sum", active: true },
  { field_id: "admin_name", label: "Admin name", active: true },
  { field_id: "attachments", label: "Attachments", active: true }
]

const initialState = {
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumnsAdmin: [...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    created_timestamp: null,
    user_full_name: [],
    user_email: [],
    status: []
  },

  visibleFiltersAdmin: { user_email: true, user_full_name: true },
  filterOptions: {
    status: ["Successful", "Failed", "Pending"]
  },

  payoutMethod: null,
  payoutMethodsFetching: false
}

const slice = createSlice({
  name: REQUESTS_WITHDRAWAL_SLICE_NAME,
  initialState,
  reducers: {
    requestsWithdrawalSetProp(state, action) {
      const { key, value } = action.payload
      if (["filters", "visibleFilters", "visibleFiltersAdmin"]?.includes(key)) {
        if (key === "filters") state.filtersLastUpd = new Date().getTime()
        state[key] = { ...state?.[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearRequestsWithdrawalFilters(state) {
      state.filters = initialState.filters
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- list ---------------- */
      .addCase(getRequestsWithdrawal.pending, (state) => {
        state.fetching = true
      })
      .addCase(getRequestsWithdrawal.fulfilled, (state, action) => {
        const result = action.payload
        state.fetching = false
        if (result?.results) {
          state.result = result
        } else {
          state.result = {
            count: result?.length || 0,
            results: result
          }
        }
      })
      .addCase(getRequestsWithdrawal.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- delete ---------------- */
      .addCase(deleteRequestsWithdrawal.pending, (state) => {
        state.fetching = true
      })
      .addCase(deleteRequestsWithdrawal.fulfilled, (state) => {
        state.fetching = false
      })
      .addCase(deleteRequestsWithdrawal.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- payout method ---------------- */
      .addCase(getPayoutMethod.pending, (state) => {
        state.payoutMethodsFetching = true
      })
      .addCase(getPayoutMethod.fulfilled, (state, action) => {
        state.payoutMethodsFetching = false
        state.payoutMethod = action.payload
      })
      .addCase(getPayoutMethod.rejected, (state) => {
        state.payoutMethodsFetching = false
      })

      /* ---------------- set status ---------------- */
      .addCase(setStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload
        state.result.results = state.result.results?.map((item) =>
          item.id === id ? { ...item, status } : item
        )
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, (state) => ({
        ...initialState,
        filters: state.filters,
        columnsAdmin: state.columnsAdmin,
        visibleFiltersAdmin: state.visibleFiltersAdmin
      }))

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["requests-withdrawal"]?.data
        if (!preference || preference.version !== REQUESTS_WITHDRAWAL_PREFERENCES_VERSION) return
        if (!preference.filters || !preference.columnsAdmin || !preference.visibleFiltersAdmin)
          return

        state.filters = preference.filters
        state.columnsAdmin = preference.columnsAdmin
        state.visibleFiltersAdmin = preference.visibleFiltersAdmin
      })
  }
})

export const requestsWithdrawalActions = slice.actions

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFiltersAdmin }`
// payload changes.
export const REQUESTS_WITHDRAWAL_PREFERENCES_VERSION = 1

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state) =>
  isEqual(state.requestsWithdrawal.filters, initialState.filters)

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "requestsWithdrawal",
    storage,
    whitelist: ["filters", "columnsAdmin", "visibleFiltersAdmin"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
